/** Pixel rectangles shared by the settled editor layout and the TV renderer. */
export interface GridRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function gridRect(
  item: { x: number; y: number; w: number; h: number },
  canvasWidth: number,
  rowHeight: number,
): GridRect {
  const x = Math.round((item.x * canvasWidth) / 12);
  const y = Math.round(item.y * rowHeight);
  return {
    x,
    y,
    width: Math.round(((item.x + item.w) * canvasWidth) / 12) - x,
    height: Math.round((item.y + item.h) * rowHeight) - y,
  };
}

export interface GridSegment {
  axis: 'h' | 'v';
  at: number;
  start: number;
  end: number;
}

/** Union collinear intervals: partial neighbors must not erase exposed edges. */
export function gridSegments(rects: readonly GridRect[]): GridSegment[] {
  const segments: GridSegment[] = [];
  for (const { x, y, width, height } of rects) {
    if (![x, y, width, height].every(Number.isFinite) || width <= 0 || height <= 0) continue;
    segments.push(
      { axis: 'h', at: y, start: x, end: x + width },
      { axis: 'h', at: y + height, start: x, end: x + width },
      { axis: 'v', at: x, start: y, end: y + height },
      { axis: 'v', at: x + width, start: y, end: y + height },
    );
  }
  segments.sort((a, b) => a.axis.localeCompare(b.axis) || a.at - b.at || a.start - b.start);
  const merged: GridSegment[] = [];
  for (const segment of segments) {
    const last = merged[merged.length - 1];
    if (last && last.axis === segment.axis && last.at === segment.at && segment.start <= last.end) {
      last.end = Math.max(last.end, segment.end);
    } else merged.push({ ...segment });
  }
  return merged;
}

/** One nonzero-fill path paints intersections once, including translucent themes. */
export function gridPath(
  rects: readonly GridRect[],
  width: number,
  height: number,
  lineWidth: number,
): string {
  if (
    ![width, height, lineWidth].every(Number.isFinite) ||
    width <= 0 ||
    height <= 0 ||
    lineWidth <= 0
  )
    return '';
  return gridSegments(rects)
    .map(({ axis, at, start, end }) => {
      const limit = axis === 'h' ? height : width;
      if (at < 0 || at > limit) return '';
      const thickness = Math.min(lineWidth, limit);
      const across = Math.max(0, Math.min(at - thickness / 2, limit - thickness));
      const from = Math.max(0, start);
      const to = Math.min(axis === 'h' ? width : height, end);
      if (to <= from) return '';
      const x = axis === 'h' ? from : across;
      const y = axis === 'h' ? across : from;
      const w = axis === 'h' ? to - from : thickness;
      const h = axis === 'h' ? thickness : to - from;
      return `M${x} ${y}h${w}v${h}h-${w}Z`;
    })
    .join('');
}
